<template>
  <v-card class="recent-activity-table" elevation="1">
    <v-card-title class="pb-2">
      <span class="text-h6">{{ $t('job-manager.recent-activity') }}</span>
      <v-spacer />
    </v-card-title>

    <v-card-text class="pt-0">
      <div class="table-container">
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :items-per-page="itemsPerPage"
          :loading="loading"
          :sort-by="['timestamp']"
          :sort-desc="[true]"
          class="recent-activity-datatable"
          mobile-breakpoint="768"
          hide-default-footer
        >
          <!-- Custom row styling -->
          <template v-slot:item="{ item }">
            <tr
              :class="getRowClass(item.status)"
              class="recent-activity-row"
            >
              <td class="text-caption">
                {{ formatTimestamp(item.timestamp) }}
              </td>
              <td class="text-caption">
                {{ item.jobId }}
              </td>
              <td class="text-body-2 font-weight-medium">
                <span class="definition-name">{{ item.definition }}</span>
              </td>
              <td class="text-body-2 font-weight-medium">
                <span class="definition-name">{{ item.place }}</span>
              </td>
              <td class="text-body-2">
                <v-chip
                  x-small
                  :color="getQueueColor(item.queue)"
                  text-color="white"
                  class="queue-chip"
                >
                  {{ item.queue }}
                </v-chip>
              </td>
              <td class="text-body-2">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  text-color="white"
                  class="status-badge"
                >
                  <v-icon x-small class="mr-1">
                    {{ getStatusIcon(item.status) }}
                  </v-icon>
                  {{ item.status }}
                </v-chip>
              </td>
              <td class="text-body-2">
                {{ formatDuration(item.duration) }}
              </td>
              <td class="text-body-2">
                {{ formatDuration(item.waitTime) }}
              </td>
              <td class="text-body-2 text-center">
                <v-chip
                  v-if="item.attempt > 1"
                  x-small
                  color="orange"
                  text-color="white"
                >
                  #{{ item.attempt }}
                </v-chip>
                <span v-else class="text--secondary">{{ item.attempt }}</span>
              </td>
              <td class="text-body-2">
                <v-chip
                  x-small
                  color="grey"
                  text-color="white"
                  class="host-chip"
                >
                  {{ item.host }}
                </v-chip>
              </td>
            </tr>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon large color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
              <p class="text-body-2 text--secondary mt-2 mb-0">
                {{ emptyMessage || $t('job-manager.no-recent-activity') }}
              </p>
            </div>
          </template>
        </v-data-table>

      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'RecentActivityTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    itemsPerPage: {
      type: Number,
      default: 50
    },
    emptyMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      headers: [
        {
          text: this.$t('job-manager.timestamp'),
          value: 'timestamp',
          sortable: true,
          width: '140px'
        },
        {
          text: this.$t('id'),
          value: 'jobId',
          width: '300px'
        },
        {
          text: this.$t('job-manager.definition'),
          value: 'definition',
          sortable: true,
          width: '180px'
        },
        {
          text: this.$t('job-manager.workflow-place'),
          value: 'place',
          sortable: true,
          width: '180px'
        },
        {
          text: this.$t('job-manager.queue'),
          value: 'queue',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('job-manager.status'),
          value: 'status',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('job-manager.duration'),
          value: 'duration',
          sortable: true,
          width: '100px'
        },
        {
          text: this.$t('job-manager.wait-time'),
          value: 'waitTime',
          sortable: true,
          width: '100px'
        },
        {
          text: this.$t('job-manager.attempt'),
          value: 'attempt',
          sortable: true,
          width: '80px',
          align: 'center'
        },
        {
          text: this.$t('job-manager.host'),
          value: 'host',
          sortable: true,
          width: '120px'
        }
      ]
    };
  },
  computed: {
    filteredItems() {
      let filtered = [...this.items];

      // Apply filters
      if (this.filters.userId) {
        filtered = filtered.filter(item =>
          item.userId && item.userId.toString().includes(this.filters.userId)
        );
      }

      if (this.filters.definitionName) {
        filtered = filtered.filter(item =>
          item.definition && item.definition.toLowerCase().includes(this.filters.definitionName.toLowerCase())
        );
      }

      if (this.filters.jobType) {
        filtered = filtered.filter(item => item.jobType === this.filters.jobType);
      }

      if (this.filters.queue) {
        filtered = filtered.filter(item => item.queue === this.filters.queue);
      }

      if (this.filters.status) {
        filtered = filtered.filter(item => item.status === this.filters.status);
      }

      return filtered;
    }
  },
  methods: {
    getRowClass(status) {
      return {
        'row-failed': status === 'FAILED',
        'row-cancelled': status === 'CANCELLED'
      };
    },
    getStatusColor(status) {
      const colors = {
        'SUCCESS': 'green',
        'FAILED': 'red',
        'RUNNING': 'blue',
        'PENDING': 'orange',
        'CANCELLED': 'grey'
      };
      return colors[status] || 'grey';
    },
    getStatusIcon(status) {
      const icons = {
        'SUCCESS': 'mdi-check-circle',
        'FAILED': 'mdi-close-circle',
        'RUNNING': 'mdi-play-circle',
        'PENDING': 'mdi-clock-outline',
        'CANCELLED': 'mdi-stop-circle'
      };
      return icons[status] || 'mdi-circle';
    },
    getQueueColor(queue) {
      const queueColors = {
        'jobs-fast': 'teal',
        'jobs-slow': 'orange',
        'jobs-default': 'primary'
      };
      return queueColors[queue] || 'grey';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '—';
      return new Date(timestamp).toLocaleString();
    },
    formatDuration(duration) {
      if (!duration) return '—';
      if (typeof duration === 'number') {
        return `${duration}s`;
      }
      return duration;
    },
  }
};
</script>

<style lang="scss" scoped>
.recent-activity-table {
  .table-container {
    height: 400px;
    overflow-y: auto;
  }
}

.recent-activity-datatable {
  ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
  }
}

.recent-activity-row {
  transition: background-color 0.2s ease-in-out;

  &.row-failed {
    background-color: #FEE2E2 !important;
  }

  &.row-cancelled {
    background-color: #F3F4F6 !important;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
}

.definition-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.status-badge {
  min-width: 80px;
  justify-content: center;
}

.queue-chip,
.host-chip {
  font-size: 0.7rem;
  height: 20px;
}

// Mobile responsive
@media (max-width: 768px) {
  .recent-activity-table {
    .table-container {
      height: 300px;
    }
  }

  .definition-name {
    max-width: 120px;
  }

  .recent-activity-datatable {
    ::v-deep .v-data-table__wrapper {
      font-size: 0.875rem;
    }
  }
}
</style>